import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Package, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './ClientManagement.css';
import { FAMILIAS_PREDEFINIDAS } from '../constants/familias';

const API_URL = process.env.REACT_APP_API_URL;


export default function AddProduct() {
  const navigate = useNavigate();
  
  // Estado del cliente activo
  const [activeClient, setActiveClient] = useState(null);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    proveedor_id: '',
    ref_proveedor: '',
    marca: '',
    familia: '',
    subfamilia: '',
    unidad_basica: '',
    cantidad_embalaje: '',
    precio_sin_iva: '',
    precio_ahorro_sin_iva: '0',
    precio_ahorro_con_iva: '0',
    tipo_iva: '21'
  });
  
  // Estados auxiliares
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '', isReferenceDuplicate: false });
  const [precioConIva, setPrecioConIva] = useState(0);

  // Familias del desplegable. Arranca con las 167 de constants/familias (fuente
  // unica) y se refresca desde la base si el endpoint responde, para recoger
  // las que se hayan creado despues.
  const [familiasDisponibles, setFamiliasDisponibles] = useState(FAMILIAS_PREDEFINIDAS);

  useEffect(() => {
    const cargarFamilias = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/productos/familias`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data.familias) && data.familias.length > 0) {
          setFamiliasDisponibles(data.familias);
        }
      } catch (err) {
        console.warn('No se pudieron cargar las familias del servidor:', err.message);
      }
    };
    cargarFamilias();
  }, []);


  // Cargar cliente activo desde sessionStorage al montar el componente
  useEffect(() => {
    const savedClient = sessionStorage.getItem('activeClient');
    if (savedClient) {
      try {
        const client = JSON.parse(savedClient);
        setActiveClient(client);
        fetchClientProviders(client.id);
      } catch (err) {
        console.error('Error al recuperar el cliente de la sesión:', err);
        setMessage({
          text: 'Error al cargar el cliente. Regresando a gestión de clientes...',
          type: 'danger',
          isReferenceDuplicate: false
        });
        setTimeout(() => navigate('/gestion-clientes'), 2000);
      }
    } else {
      // Si no hay cliente activo, mostrar mensaje y redirigir
      setMessage({
        text: 'No hay cliente seleccionado. Seleccione un cliente primero.',
        type: 'warning',
        isReferenceDuplicate: false
      });
      setTimeout(() => navigate('/gestion-clientes'), 2000);
    }
  }, [navigate]);
  
  // Obtener proveedores del cliente
  const fetchClientProviders = async (clientId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      
      const response = await fetch(`${API_URL}/api/clientes/${clientId}/proveedores`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener los proveedores');
      }
      
      const data = await response.json();
      setProviders(data.proveedores || []);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setMessage({
        text: 'Error al cargar los proveedores del cliente',
        type: 'danger',
        isReferenceDuplicate: false
      });
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Calcular precio con IVA automáticamente
    if (name === 'precio_sin_iva' || name === 'tipo_iva') {
      const precioSinIva = name === 'precio_sin_iva' ? parseFloat(value) || 0 : parseFloat(formData.precio_sin_iva) || 0;
      const tipoIva = name === 'tipo_iva' ? parseFloat(value) || 0 : parseFloat(formData.tipo_iva) || 0;
      const calculatedPrecioConIva = precioSinIva * (1 + (tipoIva / 100));
      setPrecioConIva(calculatedPrecioConIva);
    }
    
    // Calcular precio ahorro con IVA automáticamente
    if (name === 'precio_ahorro_sin_iva' || name === 'tipo_iva') {
      const precioAhorroSinIva = name === 'precio_ahorro_sin_iva' ? parseFloat(value) || 0 : parseFloat(formData.precio_ahorro_sin_iva) || 0;
      const tipoIva = name === 'tipo_iva' ? parseFloat(value) || 0 : parseFloat(formData.tipo_iva) || 0;
      const calculatedPrecioAhorroConIva = precioAhorroSinIva * (1 + (tipoIva / 100));
      setFormData(prev => ({
        ...prev,
        precio_ahorro_con_iva: calculatedPrecioAhorroConIva.toFixed(2)
      }));
    }
  };
  
  // Determinar el tipo de mensaje y su estilo
  const getMessageStyle = (type, isReferenceDuplicate) => {
    if (isReferenceDuplicate) {
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    }
    
    switch (type) {
      case 'success':
      case 'producto_nuevo':
      case 'producto_actualizado':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'referencia_actualizada':
        return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'danger':
      default:
        return 'bg-red-100 text-red-800 border border-red-200';
    }
  };

  // Obtener el icono apropiado para el mensaje
  const getMessageIcon = (type, isReferenceDuplicate) => {
    if (isReferenceDuplicate || type === 'referencia_actualizada') {
      return <AlertTriangle size={20} className="mr-2" />;
    }
    
    switch (type) {
      case 'success':
      case 'producto_nuevo':
      case 'producto_actualizado':
        return <CheckCircle size={20} className="mr-2" />;
      case 'warning':
        return <AlertTriangle size={20} className="mr-2" />;
      case 'danger':
      default:
        return <AlertCircle size={20} className="mr-2" />;
    }
  };
  
  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activeClient) {
      setMessage({
        text: 'No hay cliente seleccionado',
        type: 'danger',
        isReferenceDuplicate: false
      });
      return;
    }
    
    if (!formData.nombre || !formData.proveedor_id || !formData.precio_sin_iva) {
      setMessage({
        text: 'Por favor complete todos los campos obligatorios',
        type: 'danger',
        isReferenceDuplicate: false
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      
      const productData = {
        ...formData,
        cliente_id: activeClient.id,
        precio_sin_iva: parseFloat(formData.precio_sin_iva),
        precio_ahorro_sin_iva: parseFloat(formData.precio_ahorro_sin_iva),
        precio_ahorro_con_iva: parseFloat(formData.precio_ahorro_con_iva),
        tipo_iva: parseInt(formData.tipo_iva),
        proveedor_id: parseInt(formData.proveedor_id)
      };
      
      const response = await fetch(`${API_URL}/api/productos/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar el producto');
      }
      
      const result = await response.json();
      
      // Manejar diferentes tipos de respuesta del backend
      let messageType = 'success';
      let isReferenceDuplicate = false;
      
      if (result.type) {
        messageType = result.type;
      }
      
      if (result.isReferenceDuplicate) {
        isReferenceDuplicate = true;
      }
      
      setMessage({
        text: result.message || 'Producto procesado correctamente',
        type: messageType,
        isReferenceDuplicate: isReferenceDuplicate
      });
      
      // Limpiar formulario solo si es un producto nuevo
      if (messageType === 'producto_nuevo' || !isReferenceDuplicate) {
        setFormData({
          nombre: '',
          proveedor_id: '',
          ref_proveedor: '',
          marca: '',
          familia: '',
          subfamilia: '',
          unidad_basica: '',
          cantidad_embalaje: '',
          precio_sin_iva: '',
          precio_ahorro_sin_iva: '0',
          precio_ahorro_con_iva: '0',
          tipo_iva: '21'
        });
        setPrecioConIva(0);
      }
      
      // Limpiar mensaje después de más tiempo si es referencia duplicada
      const timeout = isReferenceDuplicate ? 6000 : 3000;
      setTimeout(() => setMessage({ text: '', type: '', isReferenceDuplicate: false }), timeout);
      
    } catch (error) {
      console.error('Error al guardar producto:', error);
      setMessage({
        text: error.message || 'Error al guardar el producto',
        type: 'danger',
        isReferenceDuplicate: false
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Volver a gestión de clientes
  const handleBack = () => {
    navigate('/gestion-clientes');
  };
  
  // Limpiar mensaje automáticamente
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '', isReferenceDuplicate: false });
      }, message.isReferenceDuplicate ? 8000 : 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Loading inicial
  if (loading && !activeClient) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-12rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Si no hay cliente activo
  if (!activeClient) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <div className="text-center max-w-lg">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">No hay cliente seleccionado</h2>
          <p className="text-gray-600 mb-6">
            Por favor, seleccione un cliente antes de añadir productos.
          </p>
          <button
            onClick={handleBack}
            className="btn-primary"
          >
            <ArrowLeft size={18} className="mr-2" />
            Volver a Gestión de Clientes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-md rounded-md p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-teal-600">
                Cliente Activo: {activeClient.razon_social}
              </h2>
              <div className="text-sm text-gray-500 flex flex-wrap items-center mt-1">
                {activeClient.cif && <span className="mr-4">CIF: {activeClient.cif}</span>}
                {activeClient.telefono1 && <span className="mr-4">Tel: {activeClient.telefono1}</span>}
                {activeClient.mail && <span>Email: {activeClient.mail}</span>}
              </div>
            </div>
          </div>
        </div>
        
        {/* Formulario */}
        <div className="bg-white shadow-md rounded-md overflow-hidden">
          {/* Header del formulario */}
          <div className="bg-teal-500 text-white p-4">
            <h3 className="text-lg font-semibold flex items-center">
              <Package size={20} className="mr-2" />
              Añadir Producto Manualmente
            </h3>
          </div>
          
          {/* Contenido del formulario */}
          <div className="p-6">
            {/* Mensaje de estado */}
            {message.text && (
              <div className={`mb-4 p-4 rounded-md flex items-center ${getMessageStyle(message.type, message.isReferenceDuplicate)}`}>
                {getMessageIcon(message.type, message.isReferenceDuplicate)}
                <div className="flex-1">
                  {message.text}
                  {message.isReferenceDuplicate && (
                    <div className="text-sm mt-1 opacity-90">
                      ℹ️ La referencia ya existía para este cliente y proveedor.
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Columna izquierda */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del Producto *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="proveedor_id" className="block text-sm font-medium text-gray-700 mb-1">
                      Proveedor *
                    </label>
                    <select
                      id="proveedor_id"
                      name="proveedor_id"
                      value={formData.proveedor_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                    >
                      <option value="">
                        {loading ? 'Cargando proveedores...' : 'Seleccione un proveedor'}
                      </option>
                      {providers.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="ref_proveedor" className="block text-sm font-medium text-gray-700 mb-1">
                      Referencia del Proveedor
                    </label>
                    <input
                      type="text"
                      id="ref_proveedor"
                      name="ref_proveedor"
                      value={formData.ref_proveedor}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <small className="text-gray-500 text-xs mt-1 block">
                      Si esta referencia ya existe para este cliente, se actualizará el producto existente.
                    </small>
                  </div>
                  
                  <div>
                    <label htmlFor="marca" className="block text-sm font-medium text-gray-700 mb-1">
                      Marca
                    </label>
                    <input
                      type="text"
                      id="marca"
                      name="marca"
                      value={formData.marca}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="familia" className="block text-sm font-medium text-gray-700 mb-1">
                      Familia
                    </label>
                    <select
                      id="familia"
                      name="familia"
                      value={formData.familia}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Sin familia</option>
                      {familiasDisponibles.map(familia => (
                        <option key={familia} value={familia}>
                          {familia}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subfamilia — solo aplica a clínicas 131/999999. En el resto
                      se ignora aunque se rellene. */}
                  <div>
                    <label htmlFor="subfamilia" className="block text-sm font-medium text-gray-700 mb-1">
                      Subfamilia <span className="text-xs text-gray-400">(solo Cirugía/Medicina)</span>
                    </label>
                    <select
                      id="subfamilia"
                      name="subfamilia"
                      value={formData.subfamilia || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">— Sanitario general (compatible con ambos) —</option>
                      <option value="cirugia">Cirugía (solo pedidos de cirugía)</option>
                      <option value="medicina">Medicina (solo pedidos de medicina)</option>
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Solo afecta a las clínicas 131 y 999999. Si lo dejas vacío, el producto entra en cualquier pedido.
                    </p>
                  </div>
                </div>
                
                {/* Columna derecha */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="unidad_basica" className="block text-sm font-medium text-gray-700 mb-1">
                      Unidad Básica
                    </label>
                    <input
                      type="text"
                      id="unidad_basica"
                      name="unidad_basica"
                      value={formData.unidad_basica}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="cantidad_embalaje" className="block text-sm font-medium text-gray-700 mb-1">
                      Cantidad Embalaje
                    </label>
                    <input
                      type="text"
                      id="cantidad_embalaje"
                      name="cantidad_embalaje"
                      value={formData.cantidad_embalaje}
                      onChange={handleInputChange}
                      placeholder="Ej: 100 unidades/caja"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="precio_sin_iva" className="block text-sm font-medium text-gray-700 mb-1">
                      Precio sin IVA * {precioConIva > 0 && (
                        <span className="text-teal-600 font-normal">
                          (Con IVA: {precioConIva.toFixed(2)}€)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      id="precio_sin_iva"
                      name="precio_sin_iva"
                      value={formData.precio_sin_iva}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="precio_ahorro_sin_iva" className="block text-sm font-medium text-gray-700 mb-1">
                      Precio Ahorro sin IVA
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      id="precio_ahorro_sin_iva"
                      name="precio_ahorro_sin_iva"
                      value={formData.precio_ahorro_sin_iva}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="precio_ahorro_con_iva" className="block text-sm font-medium text-gray-700 mb-1">
                      Precio Ahorro con IVA
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      id="precio_ahorro_con_iva"
                      name="precio_ahorro_con_iva"
                      value={formData.precio_ahorro_con_iva}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="tipo_iva" className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de IVA *
                    </label>
                    <select
                      id="tipo_iva"
                      name="tipo_iva"
                      value={formData.tipo_iva}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="21">21%</option>
                      <option value="10">10%</option>
                      <option value="4">4%</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="flex justify-center items-center mt-8 space-x-4">
                <button
                  type="submit"
                  disabled={submitting || !activeClient || loading || providers.length === 0}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </>
                  ) : loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Cargando...
                    </>
                  ) : providers.length === 0 ? (
                    <>
                      <AlertCircle size={18} className="mr-2" />
                      Sin Proveedores
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Guardar Producto
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-outline"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Volver
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
